import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StorageIcon from "@mui/icons-material/Storage";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useSearchParams } from "react-router-dom";
import useMonitoringApi from "../api/useMonitoringApi";
import {
  MonitoringBreakdownItem,
  MonitoringCameraLog,
  MonitoringSnapshot,
  MonitoringSnapshotSummary,
} from "../state/types";

const AUTO_REFRESH_MS = 60000;

const formatNumber = (value?: number | null, digits = 0) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "--";
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value);
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "--";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
};

const formatMinutes = (value?: number | null) => {
  if (value === null || value === undefined) {
    return "--";
  }

  if (value < 60) {
    return `${value} min`;
  }

  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

const formatDuration = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined) {
    return "--";
  }

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const formatBytes = (value?: number | null) => {
  if (value === null || value === undefined) {
    return "--";
  }

  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const titleCaseLabel = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") {
    return "Unknown";
  }

  return String(value)
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getBacklogTone = (pendingCount: number) => {
  if (pendingCount === 0) {
    return "success";
  }

  if (pendingCount < 10) {
    return "warning";
  }

  return "error";
};

const getSnapshotHostname = (snapshot: MonitoringSnapshot) =>
  snapshot.host?.hostname || snapshot.hostname || snapshot.summary?.hostname || "Unknown host";

// Alerts are pre-formatted strings from the backend. We filter out classes of
// alerts that are noisy or not actionable from this UI:
//   - CMS-related alerts (this view is UMS-only — CMS alerts are misleading)
//   - Vehicles older than ~a month (support is already paged via monitoring)
const isAlertVisible = (alert: string) => {
  const lower = (alert || "").toLowerCase();
  if (lower.includes("cms")) return false;
  if (
    lower.includes("month") ||
    /\b\d{2,}\s*day/.test(lower) ||
    lower.includes("30+ day") ||
    lower.includes("stale for")
  ) {
    return false;
  }
  return true;
};

const getVisibleAlerts = (snapshot: MonitoringSnapshot) =>
  (snapshot.alerts || []).filter(isAlertVisible);

const getSnapshotSummary = (snapshot: MonitoringSnapshot): MonitoringSnapshotSummary => ({
  hostname: getSnapshotHostname(snapshot),
  platform: snapshot.summary?.platform || snapshot.host?.platform || "--",
  vehicles_total: snapshot.summary?.vehicles_total ?? snapshot.vehicles?.total ?? 0,
  ready_count: snapshot.summary?.ready_count ?? snapshot.vehicles?.ready_count ?? 0,
  not_ready_count: snapshot.summary?.not_ready_count ?? snapshot.vehicles?.not_ready_count ?? 0,
  throughput_per_hour:
    snapshot.summary?.throughput_per_hour ?? snapshot.vehicles?.throughput_per_hour ?? 0,
  error_count: snapshot.summary?.error_count ?? snapshot.vehicles?.error_count ?? 0,
  recent_error_count:
    snapshot.summary?.recent_error_count ?? snapshot.vehicles?.recent_error_count ?? 0,
  outage_failure_count:
    snapshot.summary?.outage_failure_count ?? snapshot.vehicles?.outage_failure_count ?? 0,
  retrying_count: snapshot.summary?.retrying_count ?? snapshot.vehicles?.retrying_count ?? 0,
  in_ums_transit_count:
    snapshot.summary?.in_ums_transit_count ?? snapshot.vehicles?.in_ums_transit_count ?? 0,
  cms_pending_count:
    snapshot.summary?.cms_pending_count ?? snapshot.vehicles?.cms_upload_backlog?.pending_count ?? 0,
  cms_oldest_pending_age_minutes:
    snapshot.summary?.cms_oldest_pending_age_minutes ??
    snapshot.vehicles?.cms_upload_backlog?.oldest_pending_age_minutes ??
    null,
  ums_pending_count:
    snapshot.summary?.ums_pending_count ?? snapshot.vehicles?.ums_upload_backlog?.pending_count ?? 0,
  ums_oldest_pending_age_minutes:
    snapshot.summary?.ums_oldest_pending_age_minutes ??
    snapshot.vehicles?.ums_upload_backlog?.oldest_pending_age_minutes ??
    null,
  latest_vehicle_age_minutes:
    snapshot.summary?.latest_vehicle_age_minutes ?? snapshot.vehicles?.latest_vehicle?.age_minutes ?? null,
  files_total: snapshot.summary?.files_total ?? snapshot.files?.total ?? 0,
  storage_used_percent:
    snapshot.summary?.storage_used_percent ?? snapshot.host?.storage?.used_percent ?? null,
  alerts_count: snapshot.summary?.alerts_count ?? snapshot.alerts?.length ?? 0,
  has_errors: snapshot.summary?.has_errors ?? (snapshot.vehicles?.error_count ?? 0) > 0,
  has_recent_errors:
    snapshot.summary?.has_recent_errors ?? (snapshot.vehicles?.recent_error_count ?? 0) > 0,
  has_outage_failures:
    snapshot.summary?.has_outage_failures ?? (snapshot.vehicles?.outage_failure_count ?? 0) > 0,
  has_delivery_backlog:
    snapshot.summary?.has_delivery_backlog ??
    (snapshot.vehicles?.cms_upload_backlog?.pending_count ?? 0) +
      (snapshot.vehicles?.ums_upload_backlog?.pending_count ?? 0) >
      0,
  is_retrying: snapshot.summary?.is_retrying ?? (snapshot.vehicles?.retrying_count ?? 0) > 0,
  stale_data:
    snapshot.summary?.stale_data ??
    ((snapshot.vehicles?.latest_vehicle?.age_minutes ?? 0) >= 30),
  storage_has_pressure:
    snapshot.summary?.storage_has_pressure ??
    ((snapshot.host?.storage?.used_percent ?? 0) >= 85),
});

const getIssueScore = (summary: MonitoringSnapshotSummary, alertCount: number) =>
  alertCount * 1000 +
  summary.error_count * 25 +
  summary.recent_error_count * 40 +
  summary.outage_failure_count * 50 +
  summary.ums_pending_count * 3 +
  summary.retrying_count * 20 +
  (summary.stale_data ? 60 : 0) +
  (summary.storage_has_pressure ? 40 : 0);

const MetricCard = ({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: "100%",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.3 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, mt: 0.5 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 36,
            height: 36,
            borderRadius: 1.5,
            color: theme.palette.status.accent,
            backgroundColor: theme.palette.action.hover,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
};

const SectionCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          {subtitle}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}
      {children}
    </Paper>
  );
};

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.9 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ textAlign: "right", fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

const BreakdownList = ({
  items,
  keyName,
  emptyLabel,
}: {
  items: MonitoringBreakdownItem[];
  keyName: "input_method" | "rooftop_id" | "image_type";
  emptyLabel: string;
}) => {
  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data available yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {items.map((item, index) => {
        const rawValue = item[keyName];
        const label = rawValue ?? emptyLabel;

        return (
          <Box key={`${label}-${index}`}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
              <Typography variant="body2">{titleCaseLabel(label)}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatNumber(item.count)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, items[0]?.count ? (item.count / items[0].count) * 100 : 0)}
              sx={{ height: 8, borderRadius: 999 }}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

const StatusPill = ({
  label,
  severity,
}: {
  label: string;
  severity: "error" | "warning" | "success" | "default";
}) => <Chip label={label} size="small" color={severity === "default" ? undefined : severity} variant={severity === "default" ? "outlined" : "filled"} />;

const getLogLevelTone = (
  level: string
): "error" | "warning" | "info" | "success" | "default" => {
  const normalized = (level || "").toUpperCase();
  if (normalized === "ERROR" || normalized === "CRITICAL" || normalized === "FATAL") return "error";
  if (normalized === "WARNING" || normalized === "WARN") return "warning";
  if (normalized === "INFO") return "info";
  if (normalized === "DEBUG") return "default";
  return "default";
};

const CameraLogRow = ({ log }: { log: MonitoringCameraLog }) => {
  const theme = useTheme();
  const tone = getLogLevelTone(log.level);
  const accentColor =
    tone === "error"
      ? theme.palette.error.main
      : tone === "warning"
      ? theme.palette.warning.main
      : tone === "info"
      ? theme.palette.info.main
      : theme.palette.divider;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderLeft: `4px solid ${accentColor}`,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Chip
              label={(log.level || "LOG").toUpperCase()}
              size="small"
              color={tone === "default" ? undefined : tone}
              variant={tone === "default" ? "outlined" : "filled"}
            />
            <Chip label={log.position || "Unknown"} size="small" variant="outlined" />
            {log.replayed ? <Chip label="Replayed" size="small" color="info" variant="outlined" /> : null}
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
            {log.msg}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            {log.logger}
            {log.ip ? ` · ${log.ip}` : ""}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          {formatDateTime(log.ts)}
        </Typography>
      </Stack>
    </Paper>
  );
};

const SnapshotListCard = ({
  snapshot,
  onOpen,
}: {
  snapshot: MonitoringSnapshot;
  onOpen: () => void;
}) => {
  const theme = useTheme();
  const summary = getSnapshotSummary(snapshot);
  const alertCount = getVisibleAlerts(snapshot).length;

  return (
    <Paper
      elevation={0}
      onClick={onOpen}
      sx={{
        p: 2.25,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        cursor: "pointer",
        transition: "border-color 0.18s ease, transform 0.18s ease",
        "&:hover": {
          borderColor: theme.palette.status.accent,
          transform: "translateY(-1px)",
        },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {getSnapshotHostname(snapshot)}
            </Typography>
            {alertCount > 0 ? (
              <StatusPill label={`${alertCount} alerts`} severity="error" />
            ) : (
              <StatusPill label="Healthy" severity="success" />
            )}
            {summary.stale_data ? <StatusPill label="Stale" severity="warning" /> : null}
            {summary.storage_has_pressure ? <StatusPill label="Storage" severity="warning" /> : null}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {summary.platform}
          </Typography>
          <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Typography variant="body2">
              <strong>{formatNumber(summary.ums_pending_count)}</strong> UMS backlog
            </Typography>
            <Typography variant="body2">
              <strong>{formatNumber(summary.error_count)}</strong> errors
            </Typography>
            <Typography variant="body2">
              <strong>{formatMinutes(summary.latest_vehicle_age_minutes)}</strong> since last vehicle
            </Typography>
            <Typography variant="body2">
              <strong>{formatNumber(summary.storage_used_percent, 2)}%</strong> storage
            </Typography>
          </Stack>
        </Box>

        <Stack
          direction={{ xs: "row", md: "column" }}
          spacing={1}
          alignItems={{ xs: "center", md: "flex-end" }}
          sx={{ flexShrink: 0 }}
        >
          <Typography variant="caption" color="text.secondary">
            Generated {formatDateTime(snapshot.generated_at_utc)}
          </Typography>
          <Button data-cy={`reporting-snapshot-open-${getSnapshotHostname(snapshot)}`} endIcon={<ChevronRightIcon />} onClick={onOpen}>
            Open
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

const Reporting: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const monitoringApi = useMonitoringApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snapshots, setSnapshots] = useState<MonitoringSnapshot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedHost = searchParams.get("host");

  const fetchSnapshots = useCallback(
    async (backgroundRefresh = false) => {
      if (backgroundRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        const response = await monitoringApi.getMonitoringSnapshots();
        setSnapshots(response);
        setError(response.length ? null : "Monitoring endpoint returned no data.");
      } catch (err: any) {
        setError(err?.message || "Unable to load monitoring data.");
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [monitoringApi]
  );

  useEffect(() => {
    void fetchSnapshots(false);
  }, [fetchSnapshots]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void fetchSnapshots(true);
    }, AUTO_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [fetchSnapshots]);

  const sortedSnapshots = useMemo(
    () =>
      [...snapshots].sort((left, right) => {
        const leftSummary = getSnapshotSummary(left);
        const rightSummary = getSnapshotSummary(right);
        const leftScore = getIssueScore(leftSummary, getVisibleAlerts(left).length);
        const rightScore = getIssueScore(rightSummary, getVisibleAlerts(right).length);

        if (rightScore !== leftScore) {
          return rightScore - leftScore;
        }

        return getSnapshotHostname(left).localeCompare(getSnapshotHostname(right));
      }),
    [snapshots]
  );

  const selectedSnapshot = useMemo(() => {
    if (!selectedHost) {
      return null;
    }

    return (
      sortedSnapshots.find((snapshot) => getSnapshotHostname(snapshot) === selectedHost) || null
    );
  }, [selectedHost, sortedSnapshots]);

  const fleetSummary = useMemo(() => {
    const totals = sortedSnapshots.reduce(
      (acc, snapshot) => {
        const summary = getSnapshotSummary(snapshot);
        acc.alerts += getVisibleAlerts(snapshot).length;
        acc.errors += summary.error_count;
        acc.backlog += summary.ums_pending_count;
        acc.stale += summary.stale_data ? 1 : 0;
        return acc;
      },
      { alerts: 0, errors: 0, backlog: 0, stale: 0 }
    );

    return totals;
  }, [sortedSnapshots]);

  if (isLoading && !snapshots.length) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100%", p: 4 }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading monitoring snapshot...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!selectedSnapshot) {
    return (
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.25, md: 2.75 },
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
                  Unit Reporting
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                  Fleet Snapshot
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
                  Units with the most issues rise to the top. Open any one to inspect the full snapshot.
                </Typography>
              </Box>
              <Button
                data-cy="reporting-refresh-btn"
                variant="contained"
                startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={() => void fetchSnapshots(true)}
                disabled={isRefreshing}
                sx={{
                  backgroundColor: theme.palette.status.accent,
                  "&:hover": { backgroundColor: theme.palette.status.accent },
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Paper>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <MetricCard
              title="Units"
              value={formatNumber(sortedSnapshots.length)}
              subtitle="Reporting into this endpoint"
              icon={<DirectionsCarIcon fontSize="small" />}
            />
            <MetricCard
              title="Open Alerts"
              value={formatNumber(fleetSummary.alerts)}
              subtitle="Across all reporting units"
              icon={<WarningAmberIcon fontSize="small" />}
            />
            <MetricCard
              title="UMS Backlog"
              value={formatNumber(fleetSummary.backlog)}
              subtitle="Across all reporting units"
              icon={<CloudUploadIcon fontSize="small" />}
            />
            <MetricCard
              title="Stale Units"
              value={formatNumber(fleetSummary.stale)}
              subtitle="No recent vehicle activity"
              icon={<StorageIcon fontSize="small" />}
            />
          </Box>

          <SectionCard
            title="Units"
            subtitle="Sorted by alerts, errors, backlog, and stale activity"
          >
            <Stack spacing={1.5}>
              {sortedSnapshots.map((snapshot) => (
                <SnapshotListCard
                  key={getSnapshotHostname(snapshot)}
                  snapshot={snapshot}
                  onOpen={() => setSearchParams({ host: getSnapshotHostname(snapshot) })}
                />
              ))}
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    );
  }

  const snapshot = selectedSnapshot;
  const host = snapshot.host;
  const storage = host?.storage;
  const vehicles = snapshot.vehicles;
  const files = snapshot.files;
  const umsBacklog = vehicles?.ums_upload_backlog;
  const latestVehicle = vehicles?.latest_vehicle ?? null;
  const storageUsedPercent = storage?.used_percent ?? 0;
  const summary = getSnapshotSummary(snapshot);

  const visibleAlerts = getVisibleAlerts(snapshot);

  const topMetrics = [
    {
      title: `Vehicles Captured (${snapshot.window.hours}h)`,
      value: formatNumber(vehicles?.recent_window_count),
      subtitle: `In the last ${snapshot.window.hours} hours`,
      icon: <DirectionsCarIcon fontSize="small" />,
    },
    {
      title: "Throughput",
      value: `${formatNumber(summary.throughput_per_hour, 2)}/hr`,
      subtitle: `${formatNumber(summary.ready_count)} ready, ${formatNumber(summary.not_ready_count)} not ready`,
      icon: <CloudUploadIcon fontSize="small" />,
    },
    {
      title: "UMS Upload Backlog",
      value: formatNumber(summary.ums_pending_count),
      subtitle:
        summary.ums_oldest_pending_age_minutes !== null
          ? `Oldest item ${formatMinutes(summary.ums_oldest_pending_age_minutes)}`
          : "Nothing waiting to push",
      icon: <StorageIcon fontSize="small" />,
    },
    {
      title: "Active Alerts",
      value: formatNumber(visibleAlerts.length),
      subtitle: visibleAlerts.length ? "Needs operator attention" : "No active issues detected",
      icon: <WarningAmberIcon fontSize="small" />,
    },
  ];

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        p: { xs: 2, md: 3 },
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Stack spacing={2}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, md: 2.75 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Stack spacing={1.5}>
            <Button
              data-cy="reporting-back-btn"
              startIcon={<ArrowBackIcon />}
              onClick={() => setSearchParams({})}
              sx={{ alignSelf: "flex-start", px: 0 }}
            >
              All Units
            </Button>
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
                  Unit Reporting
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {getSnapshotHostname(snapshot)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
                  Generated {formatDateTime(snapshot.generated_at_utc)}. Window started {formatDateTime(snapshot.window.start_utc)} UTC.
                </Typography>
              </Box>
              <Button
                data-cy="reporting-detail-refresh-btn"
                variant="contained"
                startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                onClick={() => void fetchSnapshots(true)}
                disabled={isRefreshing}
                sx={{
                  backgroundColor: theme.palette.status.accent,
                  "&:hover": { backgroundColor: theme.palette.status.accent },
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {topMetrics.map((metric) => (
            <MetricCard
              key={metric.title}
              title={metric.title}
              value={metric.value}
              subtitle={metric.subtitle}
              icon={metric.icon}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.3fr 0.7fr" },
            gap: 2,
          }}
        >
          <SectionCard
            title="Backlog"
            subtitle={`Monitoring window started ${formatDateTime(snapshot.window.start_utc)} UTC`}
          >
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                UMS Upload Backlog
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.75 }}>
                {formatNumber(umsBacklog?.pending_count)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Oldest item {formatMinutes(umsBacklog?.oldest_pending_age_minutes)}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
                <Chip
                  label={umsBacklog?.pending_count ? "Needs attention" : "Clear"}
                  color={getBacklogTone(umsBacklog?.pending_count ?? 0)}
                  size="small"
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.75 }}>
                Sample VINs
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {((umsBacklog?.sample_vins?.length ?? 0) > 0 ? umsBacklog?.sample_vins : ["No queued VINs"])?.map(
                  (vin) => (
                    <Chip key={vin} label={vin} variant="outlined" size="small" />
                  )
                )}
              </Stack>
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                gap: 1.5,
              }}
            >
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Error Count
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
                  {formatNumber(vehicles?.error_count)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Recent Errors
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
                  {formatNumber(vehicles?.recent_error_count)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Retrying
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
                  {formatNumber(vehicles?.retrying_count)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  UMS Transit
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25 }}>
                  {formatNumber(vehicles?.in_ums_transit_count)}
                </Typography>
              </Paper>
            </Box>
          </SectionCard>

          <SectionCard title="Host" subtitle={host?.hostname || "Unit host"}>
            <DetailRow label="Hostname" value={host?.hostname || "--"} />
            <Divider />
            <DetailRow label="Latest capture" value={formatMinutes(latestVehicle?.age_minutes)} />
            <Divider />
            <DetailRow label="Uptime" value={formatDuration(host?.uptime_seconds)} />
            <Divider />
            <DetailRow label="Storage" value={`${formatNumber(storage?.used_percent, 2)}% used`} />
            <Divider />
            <DetailRow label="Free space" value={formatBytes(storage?.free_bytes)} />
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Storage utilization
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {formatNumber(storage?.used_percent, 2)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, storageUsedPercent)}
                color={storageUsedPercent >= 90 ? "error" : storageUsedPercent >= 75 ? "warning" : "success"}
                sx={{ height: 10, borderRadius: 999 }}
              />
            </Box>
          </SectionCard>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.1fr" },
            gap: 2,
          }}
        >
          <SectionCard title="Alerts" subtitle="Only the items worth checking right now">
            {visibleAlerts.length ? (
              <Stack spacing={1}>
                {visibleAlerts.map((alert) => (
                  <Alert key={alert} severity="warning">
                    {alert}
                  </Alert>
                ))}
              </Stack>
            ) : (
              <Alert severity="success">No active alerts.</Alert>
            )}
          </SectionCard>

          <SectionCard title="Latest Vehicle" subtitle={latestVehicle ? `VIN ${latestVehicle.vin}` : "No vehicle found"}>
            {latestVehicle ? (
              <Stack spacing={1.25}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <Chip label={latestVehicle.ready ? "Ready" : "Not Ready"} color={latestVehicle.ready ? "success" : "default"} size="small" />
                  <Chip
                    label={latestVehicle.ready_for_ums_upload ? "Ready for UMS" : "Waiting on UMS"}
                    color={latestVehicle.ready_for_ums_upload ? "success" : "warning"}
                    size="small"
                  />
                  <Chip label={latestVehicle.pushed_cms ? "CMS Pushed" : "CMS Pending"} color={latestVehicle.pushed_cms ? "success" : "warning"} size="small" />
                  <Chip label={latestVehicle.pushed_ums ? "UMS Pushed" : "UMS Pending"} color={latestVehicle.pushed_ums ? "success" : "warning"} size="small" />
                  {latestVehicle.error ? <Chip label="Error" color="error" size="small" /> : null}
                </Box>

                <Divider />
                <DetailRow label="Created" value={formatDateTime(latestVehicle.created_at_utc)} />
                <Divider />
                <DetailRow label="Age" value={formatMinutes(latestVehicle.age_minutes)} />
                <Divider />
                <DetailRow label="Input Method" value={titleCaseLabel(latestVehicle.input_method)} />
                <Divider />
                <DetailRow label="Rooftop" value={latestVehicle.rooftop_id || "--"} />
                <Divider />
                <DetailRow label="Retries" value={formatNumber(latestVehicle.retries)} />

                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Image Counts
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                    {Object.entries(latestVehicle.image_counts).map(([key, value]) => (
                      <Chip key={key} label={`${titleCaseLabel(key)} ${formatNumber(value)}`} variant="outlined" size="small" />
                    ))}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No latest vehicle data available.
              </Typography>
            )}
          </SectionCard>
        </Box>

        <SectionCard title="Breakdowns" subtitle="How recent captures were collected">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                Input Methods
              </Typography>
              <BreakdownList items={vehicles?.input_method_breakdown || []} keyName="input_method" emptyLabel="Phone" />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                File Types
              </Typography>
              <BreakdownList items={files?.image_type_breakdown || []} keyName="image_type" emptyLabel="Unknown" />
            </Box>
          </Box>
        </SectionCard>

        <SectionCard
          title="Camera Logs"
          subtitle={
            snapshot.camera_logs?.length
              ? `${formatNumber(snapshot.camera_logs.length)} recent ${
                  snapshot.camera_logs.length === 1 ? "entry" : "entries"
                } from cameras on this unit`
              : "Recent log entries forwarded from cameras on this unit"
          }
        >
          {snapshot.camera_logs?.length ? (
            <Stack spacing={1.25}>
              {[...snapshot.camera_logs]
                .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
                .map((log, index) => (
                  <CameraLogRow key={`${log.ts}-${log.ip}-${index}`} log={log} />
                ))}
            </Stack>
          ) : (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <VideocamIcon fontSize="small" color="disabled" />
              <Typography variant="body2" color="text.secondary">
                No camera logs in the current window.
              </Typography>
            </Stack>
          )}
        </SectionCard>

        <SectionCard title="Details" subtitle="Host and storage context">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.5,
            }}
          >
            <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Platform
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {host?.platform || "--"}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Storage Target
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {storage?.target_dir || "--"}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Used Space
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {formatBytes(storage?.used_bytes)}
              </Typography>
            </Paper>
          </Box>
          {storage?.error ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {storage.error}
            </Alert>
          ) : null}
        </SectionCard>
      </Stack>
    </Box>
  );
};

export default Reporting;
